import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import AnonymousWelcome from '../components/Crew/AnonymousWelcome';
import CrewView from '../components/Crew/CrewView';
import SessionErrorDisplay from '../components/Crew/SessionErrorDisplay';
import CampaignDashboardLayout from '../components/layout/CampaignDashboardLayout';
import {
  saveParticipantSession,
  loadParticipantSession,
  cleanupExpiredParticipantSessions,
  removeParticipantSession
} from '../general-utilities/participantSessionStorage';
import { CrewService } from '../services/CrewService';
import { ValidationException } from '../services/Exceptions';
import sessionStore from '../stores/SessionStore';

export type SessionStatus = 'loading' | 'enabled' | 'disabled' | 'error' | 'ended';
const SESSION_DISABLED = 'SESSION_DISABLED';

const Crew: React.FC = observer(() => {
  const { sessionId, deviceUuid } = useParams<{ sessionId?: string; deviceUuid?: string }>();
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<SessionStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { isAuthenticated } = sessionStore;

  useEffect(() => {
    cleanupExpiredParticipantSessions();

    if (!sessionId || !deviceUuid) {
      return;
    }

    // Reset error state when sessionId or deviceUuid changes
    setErrorStatus('loading');
    setErrorMessage(null);

    if (isAuthenticated) {
      CrewService.joinSharedSession(sessionId)
        .then((response) => {
          if (response && response.id) {
            setParticipantId(response.id);
            setErrorStatus('enabled');
            setErrorMessage(null);
          }
        })
        .catch((error) => {
          if (error instanceof ValidationException) {
            // Check if error indicates session is disabled
            const errorMsg = error.message || '';
            const isDisabledError = errorMsg === SESSION_DISABLED;

            if (isDisabledError) {
              setErrorStatus('disabled');
              setErrorMessage('disabled');
              setParticipantId(null);
            } else {
              setErrorStatus('error');
              setErrorMessage('server_error');
              setParticipantId(null);
            }
          } else {
            setErrorStatus('error');
            setErrorMessage('server_error');
            setParticipantId(null);
          }
        });
      return;
    }

    const storedSession = loadParticipantSession(sessionId);
    if (storedSession && storedSession.deviceUuid === deviceUuid) {
      setParticipantId(storedSession.participantId);
      setErrorStatus('enabled');
      setErrorMessage(null);
    }
  }, [sessionId, deviceUuid, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && sessionId) {
      removeParticipantSession(sessionId);
    }
  }, [isAuthenticated, sessionId]);

  useEffect(() => {
    return () => {
      cleanupExpiredParticipantSessions();
    };
  }, []);

  const handleAnonymousJoin = (name: string) => {
    if (!sessionId || !deviceUuid) {
      return;
    }

    // Reset error state when attempting to join
    setErrorStatus('loading');
    setErrorMessage(null);

    CrewService.joinSharedSessionAnonymous(sessionId, name)
      .then((response) => {
        if (response && response.id) {
          const newParticipantId = response.id;
          setParticipantId(newParticipantId);
          setErrorStatus('enabled');
          setErrorMessage(null);

          if (!isAuthenticated) {
            saveParticipantSession(newParticipantId, sessionId, deviceUuid, name);
          }
        }
      })
      .catch((error) => {
        if (error instanceof ValidationException) {
          // Check if error indicates session is disabled
          const errorMsg = error.message || '';
          const isDisabledError = errorMsg === SESSION_DISABLED;

          if (isDisabledError) {
            setErrorStatus('disabled');
            setErrorMessage('disabled');
            setParticipantId(null);
          } else {
            setErrorStatus('error');
            setErrorMessage('server_error');
            setParticipantId(null);
          }
        } else {
          setErrorStatus('error');
          setErrorMessage('server_error');
          setParticipantId(null);
        }
      });
  };

  // Show error display if there's an error and no participant
  if (errorStatus !== 'loading' && errorStatus !== 'enabled' && !participantId) {
    return (
      <SessionErrorDisplay
        status={errorStatus === 'ended' ? 'disabled' : errorStatus}
        error={errorMessage}
        isAuthenticated={isAuthenticated}
      />
    );
  }

  return (
    <CampaignDashboardLayout>
      {!isAuthenticated && !participantId && <AnonymousWelcome onJoin={handleAnonymousJoin} />}
      {participantId && sessionId && deviceUuid && (
        <CrewView
          participantId={participantId}
          sessionId={sessionId}
          deviceUuid={deviceUuid}
          onSessionEnded={() => {
            setErrorStatus('ended');
            setErrorMessage('disabled');
            setParticipantId(null);
          }}
        />
      )}
    </CampaignDashboardLayout>
  );
});

export default Crew;
